import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { sortProductsFlat } from '../utils/inventorySorting';
import { round2 } from '../utils/lineMath';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';

const TAX_RATE = 0.06;

function QuickPriceLookup() {
  const { products } = useData();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [priceLevel, setPriceLevel] = useState('A');
  const [priceList, setPriceList] = useState([]);

  // Restore saved list + level on mount
  useEffect(() => {
    try {
      const savedList = localStorage.getItem('quickPriceList');
      if (savedList) setPriceList(JSON.parse(savedList));
      const savedLevel = localStorage.getItem('quickPriceLevel');
      if (savedLevel) setPriceLevel(savedLevel);
    } catch (e) {
      console.error('Error loading saved quick-price state:', e);
    }
  }, []);

  // Persist whenever list or level changes
  useEffect(() => {
    try { localStorage.setItem('quickPriceList', JSON.stringify(priceList)); } catch {}
  }, [priceList]);

  useEffect(() => {
    try { localStorage.setItem('quickPriceLevel', priceLevel); } catch {}
  }, [priceLevel]);

  // Category options generated from the actual product data so every option
  // returns results — gate products are imported with whatever category string
  // the CSV used, which may not match a hardcoded list.
  const availableCategories = useMemo(() => {
    const cats = new Set();
    products.forEach(p => { if (p.category) cats.add(p.category.trim()); });
    return Array.from(cats).sort();
  }, [products]);

  const filteredProducts = sortProductsFlat(products.filter(p => {
    if (p.isActive === false) return false;
    const term = searchTerm.trim().toLowerCase();
    const matchSearch = !term ||
      p.description?.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term);
    const matchCategory = filterCategory === 'All' ||
      (p.category || '').trim().toLowerCase() === filterCategory.trim().toLowerCase();
    return matchSearch && matchCategory;
  }));

  const getCurrentPrice = (item) => {
    if (priceLevel === 'A') return parseFloat(item.priceA) || 0;
    if (priceLevel === 'B') return parseFloat(item.priceB) || 0;
    if (priceLevel === 'C') return parseFloat(item.priceC) || 0;
    return 0;
  };

  const addToList = (product) => {
    const existing = priceList.find(item => item.productId === product.id);
    if (existing) {
      setPriceList(prev => prev.map(item =>
        item.productId === product.id ? { ...item, qty: item.qty + 1 } : item
      ));
    } else {
      setPriceList(prev => [...prev, {
        id: Date.now() + Math.random(),
        productId: product.id,
        sku: product.sku,
        description: product.description,
        priceA: product.priceA,
        priceB: product.priceB,
        priceC: product.priceC,
        unit: product.unit,
        qty: 1,
      }]);
    }
    setSearchTerm('');
  };

  const removeFromList = (id) => {
    setPriceList(prev => prev.filter(item => item.id !== id));
  };

  const updateQty = (id, qty) => {
    setPriceList(prev => prev.map(item =>
      item.id === id ? { ...item, qty: Math.max(1, qty) } : item
    ));
  };

  const clearList = () => {
    if (window.confirm('Clear all items from the list?')) {
      setPriceList([]);
    }
  };

  // Same rounding rule as document totals: sum the ROUNDED per-row totals shown
  // in the list, then round tax and total off those, so the rows add up exactly.
  const subtotal = round2(priceList.reduce((sum, item) => sum + round2(item.qty * getCurrentPrice(item)), 0));
  const tax = round2(subtotal * TAX_RATE);
  const total = round2(subtotal + tax);

  return (
    <>
      <Sidebar />
      <div className="min-h-screen" style={{ marginLeft: SIDEBAR_W, background: '#CDE7F5' }}>

      {/* Main */}
      <div className="p-4 md:p-6 max-w-7xl mx-auto">

        {/* Slim header row */}
        <div className="rounded-xl p-4 mb-4 flex items-center justify-between flex-wrap gap-3" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <div>
            <h1 className="font-bold text-xl" style={{ color: '#03045E' }}>⚡ Quick Price Lookup</h1>
            <p className="text-xs" style={{ color: '#6B7C93' }}>Field price reference tool</p>
          </div>
          {/* Price Level selector */}
          <div className="rounded-lg p-1 flex gap-1" style={{ background: '#E6F1FB' }}>
            {['A', 'B', 'C'].map(level => {
              const on = priceLevel === level;
              return (
                <button
                  key={level}
                  onClick={() => setPriceLevel(level)}
                  className="px-4 py-2 rounded font-bold transition"
                  style={{
                    background: on ? '#0077B6' : 'transparent',
                    color: on ? '#FFFFFF' : '#6B7C93',
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  Level {level}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-6">

          {/* Left — Search & Results (60%) */}
          <div className="lg:col-span-3 space-y-4">
            <div className="rounded-xl p-4" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
              <input
                type="text"
                placeholder="Search by description or SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                style={{ width: '100%', fontSize: 18, border: '1px solid #E3ECF5', borderRadius: 12, padding: '16px 20px', color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                autoFocus
              />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                style={{ marginTop: 12, width: '100%', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 16px', background: '#FFFFFF', color: '#03045E', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}
              >
                <option value="All">All Categories</option>
                {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            <div className="rounded-xl overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
              {products.length === 0 ? (
                <div className="p-10 text-center" style={{ color: '#6B7C93' }}>
                  <p className="text-lg">No products loaded yet.</p>
                  <p className="text-sm mt-1">If this persists, go to Settings → Refresh Products.</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-10 text-center" style={{ color: '#6B7C93' }}>
                  <p className="text-lg">No products match.</p>
                  <p className="text-sm mt-1">Try a different search or category.</p>
                </div>
              ) : (
                <>
                  {filteredProducts.slice(0, 50).map(product => (
                    <div
                      key={product.id}
                      className="p-4 flex items-center justify-between transition"
                      style={{ borderBottom: '1px solid #E3ECF5' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#E6F1FB'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div className="flex-1 min-w-0 pr-4">
                        <p className="font-bold text-base" style={{ color: '#03045E' }}>{product.description}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#6B7C93' }}>
                          SKU: <span style={{ color: '#0077B6', fontFamily: 'monospace' }}>{product.sku}</span> {product.category ? `| ${product.category}` : ''} {product.unit ? `| ${product.unit}` : ''}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-2">
                          <span className="text-sm font-bold" style={{ color: priceLevel === 'A' ? '#0077B6' : '#6B7C93' }}>
                            A: ${(parseFloat(product.priceA) || 0).toFixed(2)}
                          </span>
                          <span className="text-sm font-bold" style={{ color: priceLevel === 'B' ? '#0077B6' : '#6B7C93' }}>
                            B: ${(parseFloat(product.priceB) || 0).toFixed(2)}
                          </span>
                          <span className="text-sm font-bold" style={{ color: priceLevel === 'C' ? '#0077B6' : '#6B7C93' }}>
                            C: ${(parseFloat(product.priceC) || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => addToList(product)}
                        className="font-bold px-6 py-3 rounded-lg text-base flex-shrink-0"
                        style={{ minHeight: 48, background: '#0077B6', border: 'none', color: '#fff', cursor: 'pointer' }}
                      >
                        ➕ Add
                      </button>
                    </div>
                  ))}
                  {filteredProducts.length > 50 && (
                    <div className="p-3 text-center text-xs" style={{ background: '#CDE7F5', borderTop: '1px solid #E3ECF5', color: '#6B7C93' }}>
                      Showing first 50 of {filteredProducts.length}. Refine your search to see more.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right — My List (40%) */}
          <div className="lg:col-span-2">
            <div className="rounded-xl p-4 lg:sticky lg:top-4" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg" style={{ color: '#03045E' }}>📋 My List</h3>
                {priceList.length > 0 && (
                  <button
                    onClick={clearList}
                    className="text-sm font-bold"
                    style={{ background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer' }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {priceList.length === 0 ? (
                <div className="text-center py-12" style={{ color: '#6B7C93' }}>
                  <p className="text-lg">Empty list</p>
                  <p className="text-sm mt-1">Add products from the search to build your list</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                    {priceList.map(item => {
                      const unit = getCurrentPrice(item);
                      return (
                        <div key={item.id} className="rounded-lg p-3" style={{ background: '#CDE7F5', border: '1px solid #E3ECF5' }}>
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-semibold text-sm flex-1 pr-2" style={{ color: '#03045E' }}>{item.description}</p>
                            <button
                              onClick={() => removeFromList(item.id)}
                              className="font-bold text-lg leading-none flex-shrink-0"
                              style={{ background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer' }}
                              aria-label="Remove item"
                            >
                              ✕
                            </button>
                          </div>
                          <p className="text-xs mb-2" style={{ color: '#6B7C93' }}>SKU: <span style={{ color: '#0077B6', fontFamily: 'monospace' }}>{item.sku}</span></p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => updateQty(item.id, Math.max(1, item.qty - 1))}
                                className="w-8 h-8 rounded-lg font-bold text-lg leading-none"
                                style={{ background: '#E6F1FB', border: '1px solid #E3ECF5', color: '#03045E', cursor: 'pointer' }}
                                aria-label="Decrease qty"
                              >
                                −
                              </button>
                              <input
                                type="number"
                                value={item.qty}
                                onChange={(e) => updateQty(item.id, parseInt(e.target.value) || 1)}
                                onFocus={(e) => e.target.select()}
                                className="w-16 text-center rounded-lg py-1 font-bold"
                                style={{ border: '1px solid #E3ECF5', color: '#03045E', background: '#FFFFFF', outline: 'none' }}
                                min="1"
                              />
                              <button
                                onClick={() => updateQty(item.id, item.qty + 1)}
                                className="w-8 h-8 rounded-lg font-bold text-lg leading-none"
                                style={{ background: '#E6F1FB', border: '1px solid #E3ECF5', color: '#03045E', cursor: 'pointer' }}
                                aria-label="Increase qty"
                              >
                                +
                              </button>
                            </div>
                            <span className="text-sm" style={{ color: '#6B7C93' }}>×</span>
                            <span className="font-bold" style={{ color: '#0077B6' }}>${unit.toFixed(2)}</span>
                            <span className="text-sm ml-auto" style={{ color: '#6B7C93' }}>
                              = <span className="font-bold" style={{ color: '#0F6E56' }}>${round2(item.qty * unit).toFixed(2)}</span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals */}
                  <div className="mt-4 pt-4" style={{ borderTop: '2px solid #E3ECF5' }}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: '#6B7C93' }}>Subtotal</span>
                      <span className="font-bold text-lg" style={{ color: '#03045E' }}>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm" style={{ color: '#6B7C93' }}>Tax (6% MD)</span>
                      <span className="font-bold text-lg" style={{ color: '#03045E' }}>${tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2" style={{ borderTop: '1px solid #E3ECF5' }}>
                      <span className="font-bold text-xl" style={{ color: '#03045E' }}>TOTAL</span>
                      <span className="font-bold text-2xl" style={{ color: '#0077B6' }}>${total.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-center mt-3 italic" style={{ color: '#6B7C93' }}>
                      Reference only — not saved to Firestore
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
      </div>
    </>
  );
}

export default QuickPriceLookup;
